import { useEffect, useState, type KeyboardEvent } from 'react';
import api from '@/api/engine';

const PRESET_NAMES = ['Muse', 'Scribe', 'Spark', 'Shadow', 'Partner'] as const;

interface PersonaNamePickerProps {
  initialName?: string;
  /** Called after a successful save via tenant persona API (for syncing category-backed settings). */
  onSaved?: () => void;
}

export function PersonaNamePicker({ initialName = '', onSaved }: PersonaNamePickerProps) {
  const [personaName, setPersonaName] = useState(initialName);
  const [draftName, setDraftName] = useState(initialName);
  const [showCustomInput, setShowCustomInput] = useState(!initialName.trim());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPersonaName(initialName);
    setDraftName(initialName);
    setShowCustomInput(
      !initialName.trim() || !PRESET_NAMES.includes(initialName as typeof PRESET_NAMES[number]),
    );
  }, [initialName]);

  const saveName = async (name: string) => {
    const trimmed = name.trim().slice(0, 20);
    if (!trimmed) {
      setError('Name cannot be empty');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await api.settings.update({ persona_name: trimmed });
      setPersonaName(response.persona_name);
      setDraftName(response.persona_name);
      onSaved?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save name');
      setDraftName(personaName);
    } finally {
      setLoading(false);
    }
  };

  const handlePresetClick = async (preset: string) => {
    setShowCustomInput(false);
    setDraftName(preset);
    await saveName(preset);
  };

  const handleCustomEnter = async (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      await saveName(draftName);
    }
  };

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-900/60 p-4">
      <h3 className="mb-2 text-sm font-semibold text-gray-100">Name your assistant</h3>
      <p className="mb-4 text-xs text-gray-400">
        This is the name your assistant goes by. A name you have saved is shown as you saved it.
      </p>

      <div className="mb-3 flex flex-wrap gap-2">
        {PRESET_NAMES.map((preset) => {
          const active = personaName === preset;
          return (
            <button
              key={preset}
              type="button"
              disabled={loading}
              onClick={() => {
                void handlePresetClick(preset);
              }}
              className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                active
                  ? 'border-yellow-400 bg-yellow-400/15 text-yellow-200'
                  : 'border-gray-600 bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              {preset}
            </button>
          );
        })}

        <button
          type="button"
          disabled={loading}
          onClick={() => setShowCustomInput((v) => !v)}
          className="rounded-md border border-gray-600 bg-gray-800 px-3 py-1.5 text-xs font-medium text-gray-300 hover:bg-gray-700"
        >
          Custom...
        </button>
      </div>

      {showCustomInput && (
        <input
          value={draftName}
          maxLength={20}
          disabled={loading}
          onChange={(e) => setDraftName(e.target.value)}
          onBlur={() => {
            void saveName(draftName);
          }}
          onKeyDown={(e) => {
            void handleCustomEnter(e);
          }}
          placeholder="Name your assistant"
          className="w-full rounded-md border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-gray-100 outline-none focus:border-yellow-400"
        />
      )}

      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs text-gray-500">Current: {personaName || 'your assistant'}</span>
        {loading && <span className="text-xs text-gray-400">Saving...</span>}
      </div>

      {error && <p className="mt-2 text-xs text-red-400">{error}</p>}
    </div>
  );
}
